"use client";

import { useEffect, useRef, useState } from "react";
import { useTrackEvent } from "@/hooks/useTrackEvent";
import type { LessonVideo } from "@/lib/localLessons/lessonVideos";

type Props = { video: LessonVideo; placement: "local_lessons" | "video_page" };

export function LessonVideoPlayer({ video, placement }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loadPoster, setLoadPoster] = useState(placement === "video_page");
  const seen = useRef(new Set<string>());
  const trackEvent = useTrackEvent();

  function record(event: string) {
    const eventKey = `${video.id}:${placement}:${event}`;
    if (seen.current.has(eventKey)) return;
    seen.current.add(eventKey);
    trackEvent(event, {
      video_id: video.id,
      video_title: video.title,
      video_placement: placement,
      source_page: placement === "local_lessons" ? "/local-lessons" : `/singing-lessons/videos/${video.slug}`,
    });
  }

  useEffect(() => {
    if (placement === "video_page" || loadPoster) return;
    const element = videoRef.current;
    if (!element) return;
    // Video preload="none" does not defer its poster image.
    if (typeof IntersectionObserver === "undefined") {
      setLoadPoster(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setLoadPoster(true);
        observer.disconnect();
      }
    }, { rootMargin: "600px 0px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, [placement, loadPoster]);

  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        record("lesson_video_impression");
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(element);
    return () => observer.disconnect();
    // Each player instance records its own impression once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.id, placement]);

  return (
    <video
      ref={videoRef}
      controls
      playsInline
      preload="none"
      poster={placement === "video_page" || loadPoster ? video.poster : undefined}
      aria-label={video.title}
      className="aspect-[9/16] w-full rounded-[14px] bg-[#062f29] object-contain"
      onFocus={() => setLoadPoster(true)}
      onPlay={() => record("lesson_video_play")}
      onTimeUpdate={(event) => {
        const { currentTime, duration } = event.currentTarget;
        if (Number.isFinite(duration) && duration > 0 && currentTime / duration >= 0.5) record("lesson_video_halfway");
      }}
      onEnded={() => record("lesson_video_complete")}
    >
      <source src={video.src} type="video/mp4" />
      <track kind="captions" src={video.captions} srcLang="en" label="English captions" />
      Your browser does not support video playback.
    </video>
  );
}
